import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, X } from 'lucide-react';
import { AdminQuizQuestion, useCreateQuestion, useUpdateQuestion } from '@/hooks/useEducationAdmin';

const questionSchema = z.object({
  question_text: z.string().min(1, "Question is required").max(500, "Question must be less than 500 characters"),
  question_type: z.enum(['multiple_choice', 'true_false', 'short_answer']),
  correct_answer: z.string().min(1, "Correct answer is required"),
  explanation: z.string().optional(),
  points: z.number().min(1, "Points must be at least 1").max(100, "Points cannot exceed 100"),
  sort_order: z.number().min(0),
  difficulty_level: z.enum(['easy', 'medium', 'hard']),
  time_limit_seconds: z.number().min(0).optional(),
  feedback_correct: z.string().optional(),
  feedback_incorrect: z.string().optional(),
});

type QuestionFormData = z.infer<typeof questionSchema>;

interface QuestionEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question: AdminQuizQuestion | null;
  moduleId: string;
}

export const QuestionEditDialog: React.FC<QuestionEditDialogProps> = ({
  open,
  onOpenChange,
  question,
  moduleId,
}) => {
  const createQuestion = useCreateQuestion();
  const updateQuestion = useUpdateQuestion();
  const isEditing = !!question;
  const [options, setOptions] = useState<string[]>(['']);

  const form = useForm<QuestionFormData>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      question_text: '',
      question_type: 'multiple_choice',
      correct_answer: '',
      explanation: '',
      points: 1,
      sort_order: 0,
      difficulty_level: 'medium',
      time_limit_seconds: 0,
      feedback_correct: '',
      feedback_incorrect: '',
    },
  });

  React.useEffect(() => {
    if (question) {
      form.reset({
        question_text: question.question_text,
        question_type: question.question_type,
        correct_answer: question.correct_answer,
        explanation: question.explanation || '',
        points: question.points,
        sort_order: question.sort_order,
        difficulty_level: question.difficulty_level,
        time_limit_seconds: question.time_limit_seconds || 0,
        feedback_correct: question.feedback_correct || '',
        feedback_incorrect: question.feedback_incorrect || '',
      });
      
      if (question.options && Array.isArray(question.options)) {
        setOptions(question.options);
      } else if (question.options && typeof question.options === 'object') {
        setOptions(Object.values(question.options) as string[]);
      }
    } else {
      form.reset({
        question_text: '',
        question_type: 'multiple_choice',
        correct_answer: '',
        explanation: '',
        points: 1,
        sort_order: 0,
        difficulty_level: 'medium',
        time_limit_seconds: 0,
        feedback_correct: '',
        feedback_incorrect: '',
      });
      setOptions(['']);
    }
  }, [question, form]);

  const questionType = form.watch('question_type');

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const onSubmit = async (data: QuestionFormData) => {
    try {
      const questionData = {
        ...data,
        options: questionType === 'multiple_choice' ? options.filter(opt => opt.trim()) : null,
      };

      if (isEditing) {
        await updateQuestion.mutateAsync({
          id: question.id,
          ...questionData,
        });
      } else {
        await createQuestion.mutateAsync({
          module_id: moduleId,
          ...questionData,
        });
      }
      onOpenChange(false);
      form.reset();
      setOptions(['']);
    } catch (error) {
      // Error handling is done in the mutation hooks
    }
  };

  const isLoading = createQuestion.isPending || updateQuestion.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Question' : 'Create New Question'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the question information below.'
              : 'Create a new quiz question for this module.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="question_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Question</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="What is Bitcoin?"
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="question_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Question Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select question type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                        <SelectItem value="true_false">True/False</SelectItem>
                        <SelectItem value="short_answer">Short Answer</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="difficulty_level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Difficulty</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select difficulty" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {questionType === 'multiple_choice' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel>Answer Options</FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addOption}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add Option
                  </Button>
                </div>
                <div className="space-y-2">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                      />
                      {options.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOption(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="correct_answer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Correct Answer</FormLabel>
                  <FormControl>
                    {questionType === 'multiple_choice' ? (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select correct answer" />
                        </SelectTrigger>
                        <SelectContent>
                          {options.filter(opt => opt.trim()).map((option, index) => (
                            <SelectItem key={index} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : questionType === 'true_false' ? (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select correct answer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">True</SelectItem>
                          <SelectItem value="false">False</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input placeholder="Enter the correct answer" {...field} />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="points"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Points</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="time_limit_seconds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time Limit (seconds)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        placeholder="0 = no limit"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="explanation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Explanation (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Explain why this is the correct answer..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="feedback_correct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Correct Answer Feedback</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Great job! You got it right..."
                        className="min-h-[60px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="feedback_incorrect"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Incorrect Answer Feedback</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Not quite right. Try again..."
                        className="min-h-[60px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : isEditing ? 'Update Question' : 'Create Question'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};